import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Field, reduxForm, formValueSelector, SubmissionError, clearSubmitErrors } from 'redux-form'
import { withRouter } from 'react-router-dom'

import { css } from '@emotion/css'

import { Notification, FormFieldset, fontSize, fontWeight, spacing, Checkbox, colors, trackEvent } from '@offerzen/pinata'

import computer from '../../assets/computer-with-checklist.svg'

import Card from '../components/Card'
import { updateProgress as reduxUpdateProgress } from '../redux/modules/utility'
import { updateCandidate as reduxUpdateCandidate } from '../redux/modules/candidate'
import { nextOnboardingUrl, previousOnboardingUrl } from '../helpers/onboardingSteps'
import CardFooterNav from '../components/CardFooterNav/CardFooterNav'

const styles = {
  heading: css({
    marginBottom: spacing.medium,
    fontWeight: fontWeight.medium,
    color: colors.blackPearl,
  }),

  employmentType: css({
    '> * + *': {
      marginTop: spacing.default,
    },

    label: {
      marginBottom: 0,
    },
  }),

  notification: css({
    marginTop: spacing.default,
    marginBottom: spacing.large,
  }),

  errorBox: css({
    marginTop: spacing.default,
  }),

  footer: css({
    marginTop: spacing.large,
  }),

  fieldSet: css({
    marginBottom: spacing.large,

    '> legend': {
      fontWeight: fontWeight.medium,
      fontSize: fontSize.medium,
      marginBottom: spacing.medium,
    },
  }),
}

const trackSelectedEmploymentType = selection =>
  trackEvent('Candidate: profile action', {
    action: 'selected employment type',
    source: 'onboarding',
    selection,
  })

class EmploymentType extends Component {
  trackUser() {
    trackEvent('onboarding-employment-type-accessed')
  }

  componentDidMount() {
    this.props.updateProgress(24)
    this.props.candidate && this.trackUser()
  }

  updateProfile(data) {
    const selectedTypes = Object.keys(data).filter(type => data[type])

    if (selectedTypes.length === 0) {
      throw new SubmissionError({
        _error: 'Please select the types of employment you are looking for.',
      })
    }

    const url = nextOnboardingUrl(this.props.location.pathname)
    return this.props.updateCandidate(
      {
        employment_types: selectedTypes,
      },
      url,
    )
  }

  render() {
    return (
      <Card title="Employment type" imageUrl={computer} width={550}>
        <form onSubmit={this.props.handleSubmit(this.updateProfile.bind(this))} onClick={() => this.props.clearFormErrors()}>
          <FormFieldset legend="What types of employment are you interested in?" className={styles.fieldSet}>
            <div className={styles.employmentType}>
              <Field
                name="permanent"
                id="permanent"
                type="checkbox"
                component={Checkbox}
                label="Permanent"
                onChange={event => event.target.checked && trackSelectedEmploymentType('permanent')}
              />

              <Field
                name="contract"
                id="contract"
                type="checkbox"
                component={Checkbox}
                label="Contract (3+ months in duration)"
                onChange={event => event.target.checked && trackSelectedEmploymentType('contract')}
              />

              <Field
                name="part_time"
                id="part-time"
                type="checkbox"
                component={Checkbox}
                label="Freelance or part-time"
                onChange={event => event.target.checked && trackSelectedEmploymentType('part_time')}
              />
            </div>
          </FormFieldset>

          <If condition={this.props.partTimeSelected && !this.props.permanentSelected && !this.props.contractSelected}>
            <Notification theme="light" className={styles.notification}>
              We’re sorry, we don’t currently support freelance or part-time work. Complete your profile and we’ll get in touch if suitable
              opportunities become available.
            </Notification>
          </If>

          {this.props.error && (
            <Notification theme="error" className={styles.errorBox}>
              {' '}
              {this.props.error}{' '}
            </Notification>
          )}

          <CardFooterNav
            navigateBack={() => this.props.history.push(previousOnboardingUrl(this.props.location.pathname))}
            loading={this.props.loading}
            theme="primary"
            className={styles.footer}
          />
        </form>
      </Card>
    )
  }
}

const mapStateToProps = state => {
  const initial = state.candidate.employment_types.reduce((acc, currentValue) => {
    acc[currentValue] = true
    return acc
  }, {})

  return {
    loading: state.candidate.loading || false,
    candidate: state.candidate,
    contractSelected: formValueSelector('employmentType')(state, 'contract'),
    permanentSelected: formValueSelector('employmentType')(state, 'permanent'),
    partTimeSelected: formValueSelector('employmentType')(state, 'part_time'),
    initialValues: {
      ...initial,
    },
  }
}

const mapDispatchToProps = dispatch => ({
  updateCandidate: (candidate, nextRoute) => dispatch(reduxUpdateCandidate(candidate, nextRoute)),
  updateProgress: progress => dispatch(reduxUpdateProgress(progress)),
  clearFormErrors: () => dispatch(clearSubmitErrors('employmentType')),
})

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(
    reduxForm({
      form: 'employmentType',
    })(EmploymentType),
  ),
)
